// src/features/pdf/components/StyledFAQ.tsx
import React from 'react';
import { styled } from '@mui/material/styles';
import { Accordion as MuiAccordion, AccordionSummary as MuiAccordionSummary, AccordionDetails, Typography, Box } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

// --- Styled Components ---

const Accordion = styled(MuiAccordion)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    '&:not(:last-child)': {
        marginBottom: theme.spacing(2),
    },
    '&:before': {
        display: 'none', // Remove the default separator line
    },
    // Apply a subtle background change when expanded
    '&.Mui-expanded': {
        margin: 'auto',
        '&:not(:last-child)': {
            marginBottom: theme.spacing(2),
        },
        backgroundColor: theme.palette.action.hover,
    },
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
    padding: theme.spacing(0, 3),
    minHeight: 64,
    // When expanded, the summary remains visible with a different style
    '&.Mui-expanded': {
        minHeight: 64,
    },
    // Custom transition for the expand icon
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(0deg)', // Keep the icon as is, we will swap it
    },
    '& .MuiAccordionSummary-content': {
        margin: theme.spacing(1.5, 0),
    },
}));

// --- Main FAQ Component ---

interface FAQItem {
    q: string;
    a: string;
}

interface StyledFAQProps {
    title: string;
    description?: string;
    faqs: FAQItem[];
    t: (key: string, options?: any) => string; // Pass the t function as a prop
}

const StyledFAQ: React.FC<StyledFAQProps> = ({ title, description, faqs, t }) => {
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ my: 6, width: '100%' }}>
            <Typography sx={{ mb: 4 }} variant="h4" component="h2" gutterBottom textAlign="center">
                {title}
            </Typography>
            {description && (
                <Typography sx={{ mb: 4 }} color="text.secondary" textAlign="center">
                    {description}
                </Typography>
            )}

            {faqs.map((faq, index) => (
                <Accordion
                    key={index}
                    expanded={expanded === `panel${index}`}
                    onChange={handleChange(`panel${index}`)}
                >
                    <AccordionSummary
                        expandIcon={
                            expanded === `panel${index}` ?
                                <RemoveCircleOutlineIcon color="primary" /> :
                                <AddCircleOutlineIcon color="action" />
                        }
                        aria-controls={`panel${index}d-content`}
                        id={`panel${index}d-header`}
                    >
                        <Typography variant="h6" component="p" sx={{ fontWeight: 500 }}>
                            {t(faq.q, { ns: 'tools' })}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3 }}>
                        <Typography color="text.secondary">
                            {t(faq.a, { ns: 'tools' })}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default StyledFAQ;